import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export type IconCountSize = 14 | 16;

export interface IconCountProps {
  /** Leading glyph element. Sized by the slot, so the glyph itself can omit width/height. */
  icon: ReactNode;
  /** Numeric count rendered with `en-US` locale grouping (1234 → "1,234"). */
  count: number;
  /**
   * Icon slot square size. Defaults to 14 to match the donut center clusters
   * and event-strip rows per Figma `289:48637`/`49285`.
   */
  size?: IconCountSize;
  className?: string;
}

const SIZE_STYLES = {
  14: 'w-[14px] h-[14px]',
  16: 'w-[16px] h-[16px]',
} as const satisfies Readonly<Record<IconCountSize, string>>;

/**
 * `IconCount` — leaf molecule pairing a glyph with a numeric label, used
 * inside event-action strips, donut center clusters, and sensitivity rows.
 * Composed by `EventCountStrip`, `SensitivityBreakdownRow`, and
 * `DonutCenterIdentityCluster` (per TDEV-273 Phase 7).
 */
export function IconCount({ icon, count, size = 14, className }: IconCountProps) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span
        data-testid="icon-count-icon-slot"
        className={cn('inline-flex items-center justify-center shrink-0', SIZE_STYLES[size])}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="tabular-nums text-p2 text-dawn-black">{count.toLocaleString('en-US')}</span>
    </span>
  );
}

IconCount.displayName = 'IconCount';
